import { useMemo, useState } from 'react';

import { API } from 'api';
import { apiBasePath, apiName } from 'assets/constants';
import type { OverlayQuestionProps } from './types';
import { showOverlaySubmitErrorToast } from './toast';
import { DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { ButtonSizes, Typography } from '@repo/ui';
import { Button, ButtonColors, ButtonVariants } from 'components';
import { NpsScale } from '../../NpsScale/NpsScale';

import styles from '../AudienceFeedbackOverlay.module.scss';

const ActionPlanQuestion = ({
  kitId,
  feedbackType,
  onDone,
}: OverlayQuestionProps) => {
  const [score, setScore] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmitDisabled = useMemo(() => score == null, [score]);

  const title = 'How helpful is your Action Plan?';

  const onSubmit = async () => {
    if (isSubmitting || score == null) return;

    setIsSubmitting(true);
    try {
      await API.post(
        apiName,
        `${apiBasePath}/audienceFeedback/${kitId}`,
        {
          question: title,
          answer: String(score),
          type: feedbackType,
        },
        true,
        undefined,
        {
          tags: {
            domain: DOMAINS.FEEDBACK,
            importance: IMPORTANCE.MEDIUM,
          },
          context: {
            kitId,
            component: 'ActionPlanQuestion',
            feedbackType,
            question: title,
            answer: score,
          },
        },
      );
    } catch (err) {
      console.error(
        'Failed to submit AudienceFeedback/ActionPlanQuestion',
        err,
      );
      showOverlaySubmitErrorToast();
    } finally {
      setIsSubmitting(false);
    }

    onDone();
  };

  return (
    <div className={styles.content}>
      <Typography variant='body-m' className={styles.primaryText}>
        {title}
      </Typography>

      <Typography variant='label' className={styles.secondaryText}>
        {'(1 = not helpful, 10 = extremely helpful)'}
      </Typography>

      <div className={styles.inputRow}>
        <NpsScale label={title} value={score} onChange={setScore} />
      </div>

      <div className={styles.actionsRow}>
        <Button
          label='Next'
          onClick={onSubmit}
          disabled={isSubmitDisabled || isSubmitting}
          loading={isSubmitting}
          variant={ButtonVariants.SOLID}
          color={ButtonColors.GREEN}
          size={ButtonSizes.S}
        />
      </div>
    </div>
  );
};

export default ActionPlanQuestion;
